import { ROUTES } from '#/config/routes';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { StockTransferSourceTypeBadge } from '#/modules/commonComponents/badge/StockTransferSourceTypeBadge';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import {
  createDocumentNavLinksColumn,
  createTipHeader,
} from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import {
  readTransferDateRangeFilter,
  resolveTransferDateFilterChipDisplay,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferDateListFilter';
import { STOCK_TRANSFER_DATE_TIP } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferDateTip';
import {
  STOCK_TRANSFER_SOURCE_TYPE_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferSourceType';
import {
  STOCK_TRANSFER_STATUS_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferStatus';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { formatDateOrDash } from '#/utils/date';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function formatWarehouseLabel(warehouse) {
  if (!warehouse) {
    return '-';
  }
  return [warehouse.code, warehouse.name].filter(Boolean).join(' ') || '-';
}

export function buildStockTransferColumns({
  filters = {},
  onFilterChange,
  onTransferDateRangeChange,
  includeNavLinks = true,
  includeSourceTypeFilter = true,
  getDetailPath,
  getEditPath,
  toWarehouseLabelCache,
  canShowEdit,
} = {}) {
  const resolveDetailPath = getDetailPath
    ?? ((row) => ROUTES.stockTransferDetail(row.id));
  const resolveEditPath = getEditPath
    ?? ((row) => ROUTES.stockTransferEdit(row.id));
  const navColumn = includeNavLinks
    ? [
      createDocumentNavLinksColumn({
        getDetailPath: resolveDetailPath,
        getEditPath: resolveEditPath,
        canShowEdit,
      }),
    ]
    : [];

  const sourceTypeColumn = {
    id: 'sourceType',
    header: '來源',
    accessorKey: 'sourceType',
    cell: ({ row }) => (
      <StockTransferSourceTypeBadge sourceType={row.original.sourceType} size="sm" />
    ),
    size: 120,
    enableSorting: false,
    enableColumnFilter: includeSourceTypeFilter,
    meta: includeSourceTypeFilter
      ? createFixedMultiFilterMeta({
        key: 'sourceType',
        label: '來源',
        options: STOCK_TRANSFER_SOURCE_TYPE_FILTER_OPTIONS,
        filterValue: filters.sourceType,
        onChange: (value) => onFilterChange('sourceType', value),
      })
      : undefined,
  };

  return [
    ...navColumn,
    {
      id: 'transferNo',
      header: '轉倉單號',
      accessorKey: 'transferNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'transferNo',
        label: '轉倉單號',
        filterValue: filters.transferNo,
        onChange: (value) => onFilterChange('transferNo', value),
        placeholder: '搜尋轉倉單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <DocumentStatusBadge status={row.original.status} size="sm" />
      ),
      size: 96,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: STOCK_TRANSFER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'toWarehouse',
      header: '轉倉倉別',
      accessorFn: (row) => formatWarehouseLabel(row.toWarehouse),
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'toWarehouseId',
          label: '轉倉倉別',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.toWarehouseId,
          onChange: (value) => onFilterChange('toWarehouseId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(toWarehouseLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(toWarehouseLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'transferDate',
      header: createTipHeader('轉倉日', STOCK_TRANSFER_DATE_TIP),
      accessorKey: 'transferDate',
      accessorFn: (row) => formatDateOrDash(row.transferDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'transferDateFrom',
        label: '轉倉日',
        mode: 'range',
        filterValue: readTransferDateRangeFilter(filters),
        onChange: onTransferDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveTransferDateFilterChipDisplay,
      }),
    },
    sourceTypeColumn,
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original?.remark || '—'} />
      ),
    },
    {
      id: 'lineCount',
      header: '項目數',
      accessorFn: (row) => {
        if (Array.isArray(row.lines)) {
          return row.lines.length;
        }
        if (row.lineCount != null && row.lineCount !== '') {
          return row.lineCount;
        }
        return '—';
      },
      size: 100,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
    },
  ];
}
